import { FetchBaseQueryError } from '@reduxjs/toolkit/query';

export const isFetchBaseQueryError = (error: unknown): error is FetchBaseQueryError =>
  typeof error === 'object' && error != null && 'status' in error;

/** DATE UTILS */

/**
 * Converts a date string into a formatted timestamp string.
 *
 * @param dateString - The date string to be converted.
 * @param locale - The locale to use for formatting (e.g., 'en-US', 'es-ES'). Defaults to 'en-US'.
 * @returns The formatted timestamp string in 'hour:minute AM/PM' format.
 */
export const getTimestamp = (dateString: string, locale: string = 'en-US'): string => {
  const date = new Date(dateString);

  const options: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true, // Use 12-hour clock format
  };

  return new Intl.DateTimeFormat(locale, options).format(date);
};

/**
 * Formats a given date string into a more readable format.
 *
 * @param dateString - The date string to format.
 * @returns A string representing the formatted date in the format "Month Day, Year".
 *
 * @example
 * ```typescript
 * const formattedDate = getFormattedDate('2023-10-05');
 * console.log(formattedDate); // "October 5, 2023"
 * ```
 */
export const longFormatDate = (dateString: string, locale: string = 'en-US'): string => {
  const date = new Date(dateString);

  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };

  return new Intl.DateTimeFormat(locale, options).format(date);
};

/**
 * Formats a given date string into the specified date format.
 *
 * @param dateFormat - The desired format for the date. Supported formats are:
 *  - 'yyyy-mm-dd': Year-Month-Day
 *  - 'dd-mm-yyyy': Day-Month-Year
 *  - 'mm-dd-yyyy': Month-Day-Year
 * @param date - The date string to be formatted.
 * @returns The formatted date string.
 * @throws Will throw an error if the provided date format is unsupported.
 */
export const shortFormatDate = (
  dateFormat: 'yyyy-mm-dd' | 'dd-mm-yyyy' | 'mm-dd-yyyy',
  date: string,
) => {
  const dateObj = new Date(date);
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
  const day = String(dateObj.getDate()).padStart(2, '0');

  switch (dateFormat) {
    case 'yyyy-mm-dd':
      return `${year}-${month}-${day}`;
    case 'dd-mm-yyyy':
      return `${day}-${month}-${year}`;
    case 'mm-dd-yyyy':
      return `${month}-${day}-${year}`;
    default:
      throw new Error('Unsupported date format');
  }
};

export const formatTime = (hour: number, minute: number): string => {
  const period = hour >= 12 ? 'pm' : 'am';
  const displayHour = hour % 12 || 12; // Convert 0 to 12 for 12 AM
  const displayMinute = minute.toString().padStart(2, '0');

  return `${displayHour}:${displayMinute}${period}`;
};

/**
 * Calculates the difference in milliseconds between two dates.
 *
 * @param date1 - The first date, provided as a `Date` object or a string.
 * @param date2 - The second date, provided as a `Date` object or a string.
 * @returns The difference in milliseconds between `date2` and `date1`.
 *          A positive value indicates that `date2` is later than `date1`,
 *          while a negative value indicates the opposite.
 * @throws {Error} If either `date1` or `date2` is an invalid date.
 */
export const getTimeDiffInMs = (date1: Date | string, date2: Date | string) => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);

  if (Number.isNaN(d1.getTime()) || Number.isNaN(d2.getTime())) {
    throw new Error('Invalid date provided');
  }

  return d2.getTime() - d1.getTime();
};

export const getYearsElapsed = (startDate: Date, endDate: Date): number => {
  if (startDate > endDate) {
    return 0;
  }

  let yearsDiff = endDate.getFullYear() - startDate.getFullYear();

  const startMonth = startDate.getMonth();
  const endMonth = endDate.getMonth();
  const startDay = startDate.getDate();
  const endDay = endDate.getDate();

  if (endMonth < startMonth || (endMonth === startMonth && endDay < startDay)) {
    yearsDiff--;
  }

  return yearsDiff;
};

/**
 * Calculates a date offset by a specified number of weeks from a reference date.
 *
 * @param weeks - The number of weeks to offset (positive for future, negative for past).
 * @param fromDate - The reference date. Defaults to today.
 * @returns A new Date object offset by the specified number of weeks.
 *
 * @example
 * ```typescript
 * const fourWeeksAgo = getDateByWeeksOffset(-4);
 * const fortyTwoWeeksFromNow = getDateByWeeksOffset(42);
 * ```
 */
export const getDateByWeeksOffset = (weeks: number, fromDate: Date = new Date()): Date => {
  const DAYS_IN_WEEK = 7;
  const MS_IN_DAY = 24 * 60 * 60 * 1000;

  const normalizedFromDate = new Date(fromDate);
  normalizedFromDate.setHours(0, 0, 0, 0);
  return new Date(normalizedFromDate.getTime() + weeks * DAYS_IN_WEEK * MS_IN_DAY);
};

/**
 * Compares two dates ignoring the time component.
 *
 * @param date1 - The first date to compare.
 * @param date2 - The second date to compare.
 * @returns -1 if date1 is before date2, 1 if date1 is after date2, 0 if they are equal.
 */
export const compareDates = (date1: Date | string, date2: Date | string): -1 | 0 | 1 => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);

  d1.setHours(0, 0, 0, 0);
  d2.setHours(0, 0, 0, 0);

  if (d1.getTime() < d2.getTime()) return -1;
  if (d1.getTime() > d2.getTime()) return 1;
  return 0;
};

/**
 * Parses a "YYYY-MM-DD" date-only string as a local calendar date. `new Date(dateString)`
 * parses date-only strings as UTC midnight, so comparing the result against a local
 * `new Date()` (or mutating it with `setHours`) can silently shift by a day in timezones
 * behind UTC - this avoids that by building the date from its components directly.
 *
 * @param dateString - A date-only string in "YYYY-MM-DD" format.
 * @returns A Date object representing local midnight of that calendar date.
 */
export const parseLocalDateString = (dateString: string): Date => {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
};

/**
 * Formats a Date as "YYYY-MM-DD" using local calendar fields, without converting to UTC.
 * @param date - The date to format in the local timezone.
 * @returns A date-only string with zero-padded month and day.
 */
export const formatLocalDateString = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

/**
 * Converts seconds number into formatted countdown string in "MM:SS" format.
 * @param seconds - The number of seconds to format.
 * @returns A string representing the formatted countdown in "MM:SS" format.
 */
export const formatCountdown = (seconds: number): string => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

/**
 * Builds the 7 trailing "yyyy-MM-dd" date keys ending on `today` (oldest first), in local time.
 * Used by the Progress tab biometric widgets (blood sugar, weight, blood pressure) to key a
 * week's worth of chart data to specific calendar days.
 *
 * @param today - The reference "today" date. Defaults to the real current date.
 * @returns 7 date keys, oldest first, last entry is `today`.
 */
export const buildLastSevenDays = (today: Date = new Date()): string[] =>
  Array.from({ length: 7 }, (_, index) => {
    const date = new Date(today);
    date.setDate(today.getDate() - (6 - index));
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });
/*
 * Formats a date into a short "M/dd" chart label (e.g. "4/27").
 *
 * @param date - A Date object or date string.
 * @returns The month/day label with a zero-padded day.
 */
export const shortChartLabel = (date: Date | string): string => {
  const d =
    typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)
      ? parseLocalDateString(date)
      : new Date(date);
  const month = d.getMonth() + 1;
  const day = String(d.getDate()).padStart(2, '0');
  return `${month}/${day}`;
};

/**
 * Formats a Date as a local `yyyy-MM-dd` string (no UTC shift).
 * Use for API date-range params to avoid off-by-one in negative-UTC zones.
 */
export const toLocalYmd = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// "2024-05-22T12:00:00Z" -> "5/22/24" in the member's local time.
export const formatRequestedOn = (timestamp: string) => {
  const date = new Date(timestamp);
  return `${date.getMonth() + 1}/${date.getDate()}/${String(date.getFullYear()).slice(-2)}`;
};

/**
 * Returns which day of membership `today` is, counting the enrollment day as day 1.
 * Date-only strings are parsed as local dates to avoid a UTC off-by-one.
 *
 * @param enrollmentDate - ISO date ("YYYY-MM-DD") or timestamp the member enrolled.
 * @param today - The reference date. Defaults to now.
 * @returns The 1-based day number, or 1 if the enrollment date is in the future.
 */
export const getMembershipDayNumber = (
  enrollmentDate: string,
  today: Date = new Date(),
): number => {
  const MS_IN_DAY = 24 * 60 * 60 * 1000;
  const start = /^\d{4}-\d{2}-\d{2}$/.test(enrollmentDate)
    ? parseLocalDateString(enrollmentDate)
    : new Date(enrollmentDate);
  const end = new Date(today);
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);

  // Rounding absorbs the ±1h DST shift between local midnights.
  return Math.max(1, Math.round((end.getTime() - start.getTime()) / MS_IN_DAY) + 1);
};

/** Today at local midnight. */
export const startOfToday = (): Date => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

/** Adds (or subtracts) whole weeks by calendar day, so DST shifts don't move the time. */
export const addWeeks = (date: Date, weeks: number): Date => {
  const shifted = new Date(date);
  shifted.setDate(shifted.getDate() + weeks * 7);
  return shifted;
};

/** First day of the inclusive 7-day window that ends on `end`. */
export const getWeekStart = (end: Date): Date =>
  new Date(end.getFullYear(), end.getMonth(), end.getDate() - 6);

/** "Apr 3 - Apr 9, 2026" for a date range given as local dates. */
export const formatDateRangeLabel = (start: Date, end: Date): string => {
  const startLabel = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(
    start,
  );
  const endLabel = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(end);
  return `${startLabel} - ${endLabel}`;
};

/** "7:00 AM" for an ISO timestamp, in the member's local time. */
export const formatTimeOfDay = (timestamp: string): string =>
  new Date(timestamp).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
